import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Campaign } from "@/types/campaign";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  Target, 
  Users, 
  CheckCircle2, 
  Clock,
  Briefcase,
  MapPin,
  Activity,
  Filter,
  Check,
  Search,
  Package,
  CheckCircle,
  Rocket,
  Megaphone,
  Layers
} from "lucide-react";
import { format } from "date-fns";
import { CampaignStatusBadge } from "@/components/brand-dashboard/CampaignStatusBadge";
import { useAuthStore } from "@/stores/authStore";

export default function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { brand } = useAuthStore();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eligibleProjectsCount, setEligibleProjectsCount] = useState<number | null>(null);

  useEffect(() => {
    async function loadCampaign() {
      if (!campaignId) return;
      
      try {
        setIsLoading(true);
        // Cast the response to Campaign type since the API returns 'any'
        const data = await apiClient.getCampaign(campaignId) as Campaign;
        setCampaign(data);
        
        // Check if campaign has a hold placed and redirect
        if (data.status === 'hold') {
          navigate(`/campaigns/${campaignId}/hold-placed`);
          return;
        }
        
        // Fetch eligible projects count (only reviewed projects)
        try {
          const eligibleProjects = await apiClient.getCampaignEligibleProjects(campaignId);
          if (eligibleProjects && Array.isArray(eligibleProjects)) {
            const reviewedProjects = eligibleProjects.filter(project => project.is_reviewed === true);
            setEligibleProjectsCount(reviewedProjects.length);
          } else if (eligibleProjects && eligibleProjects.projects && Array.isArray(eligibleProjects.projects)) {
            const reviewedProjects = eligibleProjects.projects.filter(project => project.is_reviewed === true);
            setEligibleProjectsCount(reviewedProjects.length);
          }
        } catch (projectsErr) {
          console.error("Failed to load eligible projects count", projectsErr);
          // Don't set error state for this, just leave count as null
        }
      } catch (err) {
        console.error("Failed to load campaign", err);
        setError("Failed to load campaign details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }

    loadCampaign();
  }, [campaignId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-900 mx-auto mb-4"></div>
          <p className="text-neutral-500">Loading campaign details...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-neutral-50 p-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-neutral-900 mb-4">Error Loading Campaign</h1>
          <p className="text-red-600 mb-8">{error || "Campaign not found"}</p>
          <Button onClick={() => navigate("/brand-dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 font-geist pb-12">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="mb-4">
              <Link 
                to="/brand-dashboard" 
                className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Link>
            </div>
          
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-bold text-neutral-900 tracking-tight">{campaign.name}</h1>
                <CampaignStatusBadge status={campaign.status} />
              </div>
              <p className="text-neutral-500 flex items-center gap-2 text-sm">
                <span className="font-medium text-neutral-700">ID: {campaign.id.slice(0, 8)}</span>
                <span className="text-neutral-300">|</span>
                <Calendar className="w-4 h-4" />
                Created {format(new Date(campaign.created_at), "MMM d, yyyy")}
              </p>
            </div>
            
            <div className="flex gap-3">
              {/* Contextual Actions based on Status */}
              {campaign.status === 'draft' && (
                <Button className="bg-neutral-900 hover:bg-neutral-800">
                  Edit Requirements
                </Button>
              )}
              {campaign.status === 'presented' && (
                <Button className="bg-neutral-900 hover:bg-neutral-800">
                  Review Package Options
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* 3-Card Summary Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-green-700" />
                </div>
                <span className="text-sm font-medium text-neutral-500">Budget</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900">
                ${campaign.requirements?.budget_usd?.toLocaleString() || "0"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <Megaphone className="w-4 h-4 text-purple-700" />
                </div>
                <span className="text-sm font-medium text-neutral-500">Tone</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900 capitalize">
                {/* Mocking tone for now as it might not be in top level requirements */}
                Professional
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Layers className="w-4 h-4 text-blue-700" />
                </div>
                <span className="text-sm font-medium text-neutral-500">Verticals</span>
              </div>
               <div className="flex flex-wrap gap-1">
                 {campaign.requirements?.target_categories?.slice(0, 2).map((cat, i) => (
                    <span key={i} className="text-sm font-medium text-neutral-900 truncate max-w-[120px]">
                      {cat}{i < (campaign.requirements?.target_categories?.length || 0) - 1 ? "," : ""}
                    </span>
                 )) || "General"}
                 {(campaign.requirements?.target_categories?.length || 0) > 2 && (
                   <span className="text-sm text-neutral-500">+{campaign.requirements.target_categories.length - 2} more</span>
                 )}
               </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaign Progress */}
        <div className="bg-white rounded-lg border border-neutral-200 p-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-2">Campaign Progress</h2>
            <p className="text-neutral-500 text-sm max-w-2xl mx-auto">
              Inventory matches your campaign brief. Packages will be composed after the window closes based on total platform demand.
            </p>
          </div>
          
          {/* Progress Steps - Horizontal Layout */}
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {/* Step 1: Submit Requirements - Completed */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-neutral-900 flex items-center justify-center mb-3 relative">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-medium text-neutral-900 text-sm mb-1">Submit<br/>Requirements</h3>
              <p className="text-xs text-neutral-500">Completed</p>
            </div>
            
            {/* Connector Line 1 */}
            <div className="flex-1 h-px bg-neutral-900 mx-6"></div>
            
            {/* Step 2: Browse Eligible Projects - Active */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-neutral-900 border-4 border-white shadow-lg flex items-center justify-center mb-3 relative">
                <Search className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-medium text-neutral-900 text-sm mb-1">Browse Eligible<br/>Projects</h3>
              {campaign?.window_details?.is_active ? (
                <p className="text-xs text-orange-500 font-medium">• Active Now</p>
              ) : (
                <p className="text-xs text-red-500 font-medium">• Window Closed</p>
              )}
            </div>
            
            {/* Connector Line 2 */}
            <div className="flex-1 h-px bg-neutral-300 mx-6"></div>
            
            {/* Step 3: Packages Composed - Pending */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full border-2 border-neutral-300 bg-white flex items-center justify-center mb-3">
                <Package className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="font-medium text-neutral-500 text-sm mb-1">Packages<br/>Composed</h3>
              <p className="text-xs text-neutral-400">Pending</p>
            </div>
            
            {/* Connector Line 3 */}
            <div className="flex-1 h-px bg-neutral-300 mx-6"></div>
            
            {/* Step 4: Select & Confirm - Pending */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full border-2 border-neutral-300 bg-white flex items-center justify-center mb-3">
                <CheckCircle className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="font-medium text-neutral-500 text-sm mb-1">Select &<br/>Confirm</h3>
              <p className="text-xs text-neutral-400">Pending</p>
            </div>
            
            {/* Connector Line 4 */}
            <div className="flex-1 h-px bg-neutral-300 mx-6"></div>
            
            {/* Step 5: Campaign Live - Pending */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full border-2 border-neutral-300 bg-white flex items-center justify-center mb-3">
                <Rocket className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="font-medium text-neutral-500 text-sm mb-1">Campaign<br/>Live</h3>
              <p className="text-xs text-neutral-400">Pending</p>
            </div>
          </div>
        </div>


        {/* Browse Eligible Projects Card - Styled like Figma */}
        {campaign.status === 'submitted' && (
          <div className="bg-white rounded-xl border border-neutral-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-neutral-900 flex items-center justify-center shrink-0">
                <Search className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">Browse Eligible Projects</h2>
                <p className="text-neutral-600 mb-4">
                  Explore {eligibleProjectsCount !== null ? eligibleProjectsCount : '...'} validated projects that match your campaign brief. Use preference signals to help tailor your package options.
                </p>
              </div>
            </div>
            
            {/* Countdown Section */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1">BOOKING WINDOW CLOSES IN</p>
                  <p className="text-lg font-bold text-amber-900">Window Closed</p>
                  <p className="text-sm text-amber-700">Submission period ended</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-amber-700 mb-1">Packages are composed after the window closes.</p>
                  <p className="text-sm text-amber-700">Browse eligible inventory and signal your preferences.</p>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <Button
              className="w-full bg-neutral-900 hover:bg-neutral-800 text-white font-medium py-3"
              onClick={() => navigate(`/campaigns/${campaignId}/browse-eligible`)}
            >
              Preview Projects →
            </Button>
          </div>
        )}

        {/* Detailed Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Filter className="w-4 h-4 text-neutral-500" />
              Detailed Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-medium mb-3 text-neutral-900">Integration Preferences</h3>
              {campaign.requirements?.integration_preferences && campaign.requirements.integration_preferences.length > 0 ? (
                <ul className="space-y-2">
                  {campaign.requirements.integration_preferences.map((pref, i) => (
                    <li key={i} className="flex items-start text-sm text-neutral-600 bg-neutral-50 p-2 rounded">
                      <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 mt-1.5 mr-2 shrink-0" />
                      {pref}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-neutral-400 italic">No integration preferences specified</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium mb-3 text-neutral-900">Suitability Filters</h3>
              {campaign.requirements?.suitability_filters && campaign.requirements.suitability_filters.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {campaign.requirements.suitability_filters.map((filter, i) => (
                    <div key={i} className="flex items-center text-sm border border-neutral-200 rounded px-3 py-2 text-neutral-600">
                      <CheckCircle2 className="w-3 h-3 mr-2 text-green-500" />
                      {filter}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-400 italic">No suitability filters applied</p>
              )}
            </div>
          </CardContent>
        </Card>

      </main>
    </div>
  );
}
